/* eslint-disable promise/catch-or-return */
import { constants as config } from '@config';
import Store, { actions } from '@store';

const UserInfoFactory = require('./user-info');
const CompanyFactory = require('./company').instance;
const Models = {
  palettes: require('./library/palette-model'),
  colors: require('./library/color-model'),
  texts: require('./library/text-model'),
  images: require('./library/image-model'),
  logos: require('./library/logo-model'),
  icons: require('./library/icon-model'),
  fonts: require('./library/font-model'),
  folders: require('./library/folder-model'),
  videos: require('./library/video-model'),
  member: require('./library/member-font-model')
};

function LibraryFactory (opts) {
  _.bindAll(this,
    'initialize',
    'get',
    'post',
    'setCompany',
    'cacheClear',
    'initialize',
    'getResource',
    '_collectionFetch'
  );

  if (!opts) {
    opts = {};
  } else if (typeof opts === 'string') {
    opts = { librarySource: opts };
  }

  const hash = JSON.stringify(opts);

  this.librarySource = opts.librarySource;
  this.addedColors = [];
  this.collections = {};
  this.cache = {};
  this.modelCache = {};
  this.currentCompany = opts.domain;
  this.currentFolderId = null;
  this.popupMenuColorPalette = '0';
  this.isEnterprise = INKIVE.C.WHITE_LABEL_CONFIGURATION &&
    INKIVE.C.WHITE_LABEL_CONFIGURATION.subscription_plan === 'enterprise';

  if (!LibraryFactory.instances[hash]) {
    LibraryFactory.instances[hash] = this;
  }

  return LibraryFactory.instances[hash];
}

LibraryFactory.instances = {};

LibraryFactory.prototype = _.extend({}, Backbone.Events);

LibraryFactory.prototype.initialize = _.memoize(function () {
  const deferred = $.Deferred();

  $.when(
    UserInfoFactory.get(),
    CompanyFactory.getCompany()
  ).then(_.bind(function (userData, companyData) {
    this.companyData = companyData;

    const company = this.currentCompany || (companyData && companyData.id) || 'desygner';
    this.setCompany(company);

    deferred.resolve();
  }, this));

  return deferred.promise();
}, _.bind(function (opts) {
  if (!opts) {
    opts = {};
  } else if (typeof opts === 'string') {
    opts = { librarySource: opts };
  }

  return JSON.stringify(opts);
}, this));

LibraryFactory.prototype.setCompany = function (companyId) {
  this.baseUrl = INKIVE.Helpers.projectIsLinkedToRestrictions() && this.librarySource !== 'member'
    ? INKIVE.C.API_URL + 'brand/companies/' + companyId + '/assets/'
    : INKIVE.C.API_URL + 'brand/companies/' + companyId + '/library/'
  ;

  this.currentCompany = companyId;
  this.collections = {};
  this.cache = {};

  return this;
};

LibraryFactory.prototype.get = function (resource, filter, fromCurrentInstance) {
  const deferred = $.Deferred();

  const librarySource = this.librarySource;
  this
    .initialize({
      librarySource: this.librarySource,
      domain: this.currentCompany
    })
    .then(_.partial(this._collectionFetch, resource))
    .then(_.bind(function () {
      const resourceCollection = this.collections[resource];
      const results = !filter
        ? resourceCollection ? resourceCollection.toArray() : []
        : resourceCollection.filter(function (model) {
          const data = model.get('data');

          return _.all(filter, function (value, key) {
            return data[key].toString() === value.toString() || model.get(key) === value;
          });
        });

      deferred.resolve(_(results).map(model =>
        this._addSupportForSmartAssets(model.toJSON(), librarySource)
      ));
    }, this))
  ;

  return deferred.promise();
};

LibraryFactory.prototype.post = function (resource, payload) {
  const deferred = $.Deferred();

  this._collectionFetch(resource).then(_.bind(function (collection) {
    const model = collection.create(payload, {
      url: this.baseUrl + resource,
      headers: Backbone.BasicAuth.getHeader({
        username: CONFIG.credentials.token,
        password: CONFIG.credentials.hash
      })
    });

    model.on('change:id', deferred.resolve);
    model.on('error', function () {
      model.destroy();
      deferred.reject(new Error('_collectionFetch Error'));
    }, 'add-to-library-error');
  }, this));

  return deferred.promise();
};

LibraryFactory.prototype.getResource = function (resourceKey, id, force) {
  if (this.modelCache[id] && !force) return this.modelCache[id];

  const resourceModel = new Models[resourceKey]();
  resourceModel.set('id', id);
  resourceModel.baseUrl = this.baseUrl;
  resourceModel.companyData = this.companyData;

  const deferred = $.Deferred();
  const headers = Backbone.BasicAuth.getHeader({
    username: CONFIG.credentials.token,
    password: CONFIG.credentials.hash
  });
  const librarySource = this.librarySource;

  this.modelCache[id] = deferred.promise();

  resourceModel
    .fetch({ headers, error: deferred.reject })
    .then((resources) => {
      parseResources(resources);
      const resourcesWithSupportForSmartAssets = (resources ? (Array.isArray(resources) ? resources : [resources]) : [])
        .map(item => this._addSupportForSmartAssets(item, librarySource));

      Store.dispatch(actions.assets.setDefinedAssets(resourcesWithSupportForSmartAssets));

      return deferred.resolve(resourcesWithSupportForSmartAssets);
    });

  function parseResources (resourcesData) {
    let resources = Array.isArray(resourcesData) ? resourcesData : [];

    if (resourceKey.type === 'folder') {
      resources = resourcesData.data.resources;
    }

    resources.forEach((resource) => {
      const modelKey = resource.type.toLocaleLowerCase() + 's';

      if (Models[modelKey] && Models[modelKey].prototype.parse) {
        Models[modelKey].prototype.parse(resource);
      }
    });
  }

  return this.modelCache[id];
};

// TODO: remove once flag behavior is decided
LibraryFactory.prototype._addSupportForSmartAssets = function (resource, librarySource) {
  librarySource = this.librarySource || librarySource;
  const supportSmartAssets = librarySource !== 'member' &&
    !!INKIVE.C.FEATURE_FLAG?.brand_assets?.includes('show_smart_assets');

  return {
    ...resource,
    is_smart_asset: supportSmartAssets
  };
};

LibraryFactory.prototype.cacheClear = function (resource) {
  this.cache = _(this.cache).omit(resource);
  this.collections = _(this.collections).omit(resource);
};

LibraryFactory.prototype._collectionFetch = function (resource) {
  if (this.cache[resource]) return this.cache[resource];

  const deferred = $.Deferred();
  this.collections[resource] || this._initCollection(resource);
  this.cache[resource] = deferred.promise();

  const processCall = this.processMemberCall(resource);

  processCall && this.collections[resource].fetch({
    success: this._saveInStore(deferred.resolve),
    error: deferred.resolve
  });
  processCall || deferred.resolve();

  return this.cache[resource];
};

LibraryFactory.prototype._saveInStore = function (resolve) {
  return (collection, data, ...args) => {
    if (Array.isArray(data)) {
      Store.dispatch(actions.assets.setDefinedAssets(data));
    }

    return resolve.apply(this, [collection, data, ...args]);
  };
};

LibraryFactory.prototype._initCollection = function (resource) {
  const Collection = Backbone.Collection.extend({
    model: Models[resource],
    url: this.baseUrl + resource
  });

  this.collections[resource] = new Collection();
  this.collections[resource].credentials = {
    username: CONFIG.credentials.token,
    password: CONFIG.credentials.hash
  };
};

LibraryFactory.prototype.clearModelCache = function () {
  this.modelCache = {};
};

LibraryFactory.prototype.processMemberCall = function (resource) {
  if (resource !== 'member') {
    return true;
  }

  if (this.isEnterprise) {
    this.collections[resource].url = `${this.baseUrl}contents`;
  }

  return false;
};

module.exports = {
  factory: LibraryFactory,
  instance: new LibraryFactory()
};
